import { generateUuid } from '../../../helpers/string';
import {
    OrderItemResource,
    OrderLine,
    OrderResource,
    PricesResource,
} from '../../@api/Sylius';
import { transformToVariantCode } from '../../@products/Product/ProductTransformers';
import { Address, AddressType } from '../../Address/Address';
import { transformToCustomerAddress } from '../../Address/AddressTransformers';
import { calculateDiscounts, transformToDiscount, transformToProductPrice } from '../../Price/PriceTransformers';
import { CartInterface, CartItem } from './Cart';

const transformToCartItem = (resource: OrderItemResource, lines: OrderLine[] = []): CartItem => {
    const id = resource.id
        ? resource.id.toString()
        : generateUuid();

    const variantCode = transformToVariantCode(resource.variant);

    const pricing = lines.find(line => line.variant === variantCode);

    const discount = pricing?.discount
        ? transformToDiscount(pricing.discount)
        : undefined;

    const price = pricing?.price
        ? transformToProductPrice(pricing.price.payable, pricing.price.original, discount)
        : transformToProductPrice(resource.total, resource.total, discount);

    return {
        id,
        variantCode,
        stock: 0, // Value of '0', because it'll be enriched in fetchCartItem() (cartActions.ts)
        name: resource.productName || '',
        relativeUrl: resource.relativeUrl,
        quantity: resource.quantity,
        price,
    };
};

export const transformToCart = (resource: OrderResource, prices?: PricesResource): CartInterface => {
    const addresses: Address[] = [];

    if (resource.shippingAddress) {
        const address = transformToCustomerAddress(resource.shippingAddress, AddressType.shipping);
        addresses.push(address);
    }

    if (resource.billingAddress) {
        const address = transformToCustomerAddress(resource.billingAddress, AddressType.billing);
        addresses.push(address);
    }

    const items = resource.items
        ? resource.items.map(item => transformToCartItem(item, prices?.lines))
        : [];

    const discounts = prices?.discounts
        ? calculateDiscounts(prices)
        : [];

    const totalPrice = prices?.price
        ? prices.price.stillToBePaid
        : resource.total;

    const orderedAt = resource.orderedAt
        ? new Date(resource.orderedAt).toISOString()
        : undefined;

    return {
        id: resource.tokenValue || generateUuid(),
        addresses,
        items,
        subTotalPrice: resource.itemsTotal,
        handlingPrice: prices?.price?.handling || 0,
        discounts,
        totalPrice,
        orderedAt,
        loods5Id: resource.loods5Id,
    };
};
